import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Invoice } from '../types';
import { format } from 'date-fns';

const ContractorInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [generating, setGenerating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contractor/invoices');
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'warning';
      case 'SUBMITTED':
        return 'info';
      case 'CERTIFIED':
        return 'success';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'PENDING':
        return 'warning';
      case 'PAID':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleGenerateBill = async () => {
    try {
      setGenerating(true);
      const response = await api.post('/contractor/invoices/generate-draft', {
        month: selectedMonth || null, // null = current month
      });
      alert(`Draft invoice created successfully for ${response.data.summary.month}`);
      setGenerateDialogOpen(false);
      setSelectedMonth('');
      fetchInvoices();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to generate draft invoice');
    } finally {
      setGenerating(false);
    }
  };

  // Generate month options (last 3 months + current month)
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 4; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  const draftInvoices = invoices.filter(inv => inv.status === 'DRAFT');
  const rejectedInvoices = invoices.filter(inv => inv.status === 'REJECTED');
  const pendingInvoices = invoices.filter(inv => inv.status === 'PENDING');
  const paidInvoices = invoices.filter(inv => inv.status === 'PAID');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">My Invoices</Typography>
        <Button
          variant="contained"
          startIcon={<ReceiptIcon />}
          onClick={() => setGenerateDialogOpen(true)}
        >
          Generate Bill
        </Button>
      </Box>

      {draftInvoices.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have {draftInvoices.length} draft invoice(s) awaiting admin approval.
        </Alert>
      )}

      {rejectedInvoices.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have {rejectedInvoices.length} rejected invoice(s). Please review the rejection reasons and regenerate.
        </Alert>
      )}

      {pendingInvoices.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have {pendingInvoices.length} invoice(s) pending payment.
        </Alert>
      )}

      {paidInvoices.length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          You have {paidInvoices.length} paid invoice(s).
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice Number</TableCell>
              <TableCell>Period</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submission Date</TableCell>
              <TableCell>Certified Date</TableCell>
              <TableCell>Payment Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell>{invoice.invoiceNumber}</TableCell>
                <TableCell>
                  {format(new Date(invoice.startDate), 'MMM dd')} - {format(new Date(invoice.endDate), 'MMM dd, yyyy')}
                </TableCell>
                <TableCell align="right">${invoice.total.toFixed(2)}</TableCell>
                <TableCell>
                  <Chip 
                    label={invoice.status} 
                    color={getStatusColor(invoice.status) as any} 
                    size="small"
                    {...(invoice.status === 'SUBMITTED' && { variant: 'outlined', color: 'warning' })}
                  />
                </TableCell>
                <TableCell>
                  {invoice.submissionDate ? format(new Date(invoice.submissionDate), 'MMM dd, yyyy') : '-'}
                </TableCell>
                <TableCell>
                  {invoice.certifiedAt ? format(new Date(invoice.certifiedAt), 'MMM dd, yyyy') : '-'}
                </TableCell>
                <TableCell>
                  {invoice.paymentDate ? format(new Date(invoice.paymentDate), 'MMM dd, yyyy') : '-'}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => navigate(`/contractor/invoices/${invoice.id}`)}>
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Generate Bill Dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Draft Invoice</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info">
              This will create a draft invoice from all approved, unbilled hours for the selected month.
            </Alert>
            <FormControl fullWidth>
              <InputLabel>Select Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Select Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <MenuItem value="">Current Month (Recommended)</MenuItem>
                {getMonthOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Only approved hours that haven't been billed yet will be included. Each month generates a separate invoice.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleGenerateBill}
            variant="contained"
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Generate Draft Invoice'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractorInvoices;


